import { Card, CardContent } from "@/components/ui/card";
import type { StatutoryAnalysis, StatutoryAnalysisStatus } from "@/types/legislationMonitor";

type StatutoryChangesPaneProps = {
  analysis: StatutoryAnalysis | null;
  pendingMessage?: string;
  failedMessage?: string;
  emptyMessage?: string;
  notAvailableMessage?: string;
};

function Pane({
  label,
  text,
}: {
  label: string;
  text: string | null | undefined;
}) {
  const body = text?.trim();

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      {body ? (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{body}</p>
      ) : (
        <p className="text-sm text-muted-foreground">No summary available.</p>
      )}
    </div>
  );
}

function CompletedPane({
  analysis,
  emptyMessage,
}: {
  analysis: StatutoryAnalysis;
  emptyMessage: string;
}) {
  const hasHero = Boolean(analysis.law_as_it_is?.trim() || analysis.law_as_it_will_be?.trim());

  if (!hasHero) {
    if (analysis.gist?.trim()) {
      return <p className="text-sm leading-relaxed text-muted-foreground">{analysis.gist}</p>;
    }

    return (
      <Card className="border-dashed border-muted-foreground/30 bg-muted/20">
        <CardContent className="p-6 text-sm text-muted-foreground">{emptyMessage}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {analysis.gist?.trim() ? (
        <p className="text-sm font-medium leading-relaxed text-foreground">{analysis.gist}</p>
      ) : null}

      <div className="grid gap-6 md:grid-cols-2">
        <Pane label="Current Law" text={analysis.law_as_it_is} />
        <Pane label="After Enactment" text={analysis.law_as_it_will_be} />
      </div>
    </div>
  );
}

export function StatutoryChangesPane({
  analysis,
  pendingMessage = "Statutory analysis is being prepared.",
  failedMessage = "Statutory analysis could not be generated.",
  emptyMessage = "No statutory comparison is available for this bill yet.",
  notAvailableMessage = "Ordinal has not prepared a statutory comparison for this bill yet.",
}: StatutoryChangesPaneProps) {
  if (!analysis) {
    return (
      <Card className="border-dashed border-muted-foreground/30 bg-muted/20">
        <CardContent className="p-6 text-sm text-muted-foreground">{notAvailableMessage}</CardContent>
      </Card>
    );
  }

  const status: StatutoryAnalysisStatus = analysis.status;
  switch (status) {
    case "pending":
    case "processing":
      return (
        <Card className="border-dashed border-muted-foreground/30 bg-muted/20">
          <CardContent className="p-6 text-sm text-muted-foreground">{pendingMessage}</CardContent>
        </Card>
      );
    case "failed":
      return (
        <Card className="border-dashed border-destructive/40 bg-destructive/5">
          <CardContent className="p-6 text-sm text-destructive">{failedMessage}</CardContent>
        </Card>
      );
    case "completed":
      return <CompletedPane analysis={analysis} emptyMessage={emptyMessage} />;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}
